import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { NutzerService } from '../services/nutzerService';

const ProfilPage: React.FC = () => {
  const { user, updateUser } = useAuth();

  // Profildaten
  const [vorname, setVorname] = useState(user?.vorname || '');
  const [nachname, setNachname] = useState(user?.nachname || '');
  const [email, setEmail] = useState(user?.email || '');

  // Passwort-Felder
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // UI-Status
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Formularfelder synchron halten, wenn sich der User im Context ändert
  useEffect(() => {
    setVorname(user?.vorname || '');
    setNachname(user?.nachname || '');
    setEmail(user?.email || '');
  }, [user]);

  if (!user) return null;

  const handleSaveProfile = async () => {
    setProfileMsg(null);
    setSaving(true);
    try {
      const updated = await NutzerService.updateNutzer(user.id, {
        vorname,
        nachname,
        email,
      });
      updateUser({
        ...user,
        vorname: updated.vorname,
        nachname: updated.nachname,
        email: updated.email,
      });
      setProfileMsg({ type: 'success', text: 'Profil erfolgreich aktualisiert.' });
    } catch (err: any) {
      console.error('Profil-Update fehlgeschlagen:', err);
      setProfileMsg({ type: 'error', text: err.response?.data?.message || 'Fehler beim Speichern des Profils.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePassword = async () => {
    setPasswordMsg(null);

    if (!password) {
      setPasswordMsg({ type: 'error', text: 'Bitte geben Sie ein neues Passwort ein.' });
      return;
    }
    if (password !== passwordConfirm) {
      setPasswordMsg({ type: 'error', text: 'Die Passwörter stimmen nicht überein.' });
      return;
    }
    if (password.length < 6) {
      setPasswordMsg({ type: 'error', text: 'Das Passwort muss mindestens 6 Zeichen lang sein.' });
      return;
    }

    setSavingPassword(true);
    try {
      await NutzerService.updateNutzer(user.id, { password });
      setPassword('');
      setPasswordConfirm('');
      setPasswordMsg({ type: 'success', text: 'Passwort erfolgreich geändert.' });
    } catch (err: any) {
      console.error('Passwort-Update fehlgeschlagen:', err);
      setPasswordMsg({ type: 'error', text: err.response?.data?.message || 'Fehler beim Ändern des Passworts.' });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <PersonIcon color="primary" fontSize="large" />
        <Typography variant="h4">Mein Profil</Typography>
      </Box>

      {/* Profildaten */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Persönliche Daten</Typography>
        {profileMsg && <Alert severity={profileMsg.type} sx={{ mb: 1 }}>{profileMsg.text}</Alert>}

        <TextField
          label="Benutzername"
          value={user.username}
          fullWidth
          margin="normal"
          disabled
          helperText="Der Benutzername kann nicht geändert werden."
        />

        <TextField
          label="Vorname"
          value={vorname}
          onChange={(e) => setVorname(e.target.value)}
          fullWidth
          margin="normal"
        />

        <TextField
          label="Nachname"
          value={nachname}
          onChange={(e) => setNachname(e.target.value)}
          fullWidth
          margin="normal"
        />

        <TextField
          label="E-Mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="normal"
        />

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : undefined}
          >
            {saving ? 'Speichern...' : 'Profil speichern'}
          </Button>
        </Box>
      </Paper>

      {/* Passwort ändern */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Passwort ändern</Typography>
        {passwordMsg && <Alert severity={passwordMsg.type} sx={{ mb: 1 }}>{passwordMsg.text}</Alert>}

        <TextField
          label="Neues Passwort"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="normal"
        />

        <TextField
          label="Passwort bestätigen"
          type="password"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          fullWidth
          margin="normal"
          error={!!passwordConfirm && password !== passwordConfirm}
          helperText={
            passwordConfirm && password !== passwordConfirm
              ? 'Die Passwörter stimmen nicht überein.'
              : ''
          }
        />

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSavePassword}
            disabled={savingPassword || !password}
            startIcon={savingPassword ? <CircularProgress size={18} /> : undefined}
          >
            {savingPassword ? 'Speichern...' : 'Passwort ändern'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProfilPage;
